import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { MessageService } from "primeng/api";
import {
  buildLocaleFallbackCandidates,
  DEFAULT_LANGUAGE_CODE,
  getLanguageDisplayName,
  isKnownLanguageCode,
  LANGUAGE_STORAGE_KEY,
  LanguageOption,
  resolveLocaleFallback,
} from "@core/constants/languages";
import {
  TRANSLATION_LOADER_MESSAGES,
  TRANSLATION_TOAST_MESSAGES,
  USE_LOCAL_TRANSLATION_FALLBACK,
} from "@core/constants/translation.config";
import { LoaderService } from "@core/services/loader/loader.service";
import { TranslationApiService } from "./translation-api.service";
import { TranslationCacheService } from "./translation-cache.service";
import { unflattenTranslationKeys } from "./translation-flat.util";
import {
  translateOr as resolveTranslateOr,
  TranslationParams,
} from "./translate-or.util";
import { firstValueFrom, Subject } from "rxjs";

@Injectable({ providedIn: "root" })
export class TranslationService {
  private readonly transloco = inject(TranslocoService);
  private readonly loader = inject(LoaderService);
  private readonly messageService = inject(MessageService);
  private readonly translationApi = inject(TranslationApiService);
  private readonly translationCache = inject(TranslationCacheService);

  /** Populated from GET /language-translation/languages after login. */
  private availableLanguages: LanguageOption[] = [];
  private availableLanguageCodes = new Set<string>([DEFAULT_LANGUAGE_CODE]);
  private availableLanguagesLoaded = false;

  /**
   * Last language preference from GET /me (`userSelectedLanguage`).
   * Kept so later syncLanguageAccess() calls (tokenLoaded / role refresh)
   * do not fall back to en-US before or after /me has been applied.
   */
  private serverLanguagePreference: string | null = null;

  /**
   * Emits the applied locale after a user language preference change.
   * Feature pages can subscribe to refetch locale-sensitive API data.
   */
  private readonly languageAppliedSubject = new Subject<string>();
  readonly languageApplied$ = this.languageAppliedSubject.asObservable();

  /**
   * Active-language value, else the static English `fallback`.
   * Missing keys do not fall through to en-US.json.
   * Unavailable language catalogs still use n-1 / English via setLanguage.
   */
  translateOr(
    key: string,
    fallback: string,
    params?: TranslationParams,
  ): string {
    return resolveTranslateOr(this.transloco, key, fallback, params);
  }

  async init(): Promise<void> {
    // Always clear cached catalogs on startup:
    // - When using local assets: ensures new JSON keys are never hidden by stale cache.
    // - When using the live API: ensures each app load fetches a fresh catalog
    //   (avoids serving a stale API snapshot across deployments).
    this.translationCache.clearAll();
    this.resetAvailableLanguages();

    const storedLocale = this.getStoredLanguage();
    const locale = storedLocale
      ? resolveLocaleFallback(storedLocale, DEFAULT_LANGUAGE_CODE)
      : DEFAULT_LANGUAGE_CODE;

    if (storedLocale && locale !== storedLocale) {
      this.persistLanguage(locale);
    }

    // Translation catalog is loaded post-login via syncLanguageAccess().
    // No HTTP call is made here — the auth token is not yet available at startup.
    this.ensureTranslocoLangAvailable(locale);
    this.transloco.setActiveLang(locale);
  }

  /**
   * Restore language access after login / role resolution.
   * Pass `userSelectedLanguage` only from GET /me so the server preference
   * is cached; other callers omit it and reuse the cached value.
   */
  async syncLanguageAccess(
    canSwitchLanguage: boolean,
    options?: { userSelectedLanguage?: string | null },
  ): Promise<void> {
    // Only LT-eligible roles (Language Preference) should surface missing-key
    // console warnings. Super Admin / GSE are outside LT.
    this.setMissingKeyLogging(canSwitchLanguage);

    if (!canSwitchLanguage) {
      // Role does not allow language switching — load the default locale catalog
      // so English translations are available (the Transloco loader returns {}
      // because the cache was cleared in init()).
      this.serverLanguagePreference = null;
      await this.loadCatalogForLocale(DEFAULT_LANGUAGE_CODE);
      this.ensureTranslocoLangAvailable(DEFAULT_LANGUAGE_CODE);
      this.transloco.setActiveLang(DEFAULT_LANGUAGE_CODE);
      this.persistLanguage(DEFAULT_LANGUAGE_CODE);
      return;
    }

    // When /me supplies a preference, cache it. Calls without options
    // (tokenLoaded / early init) keep the last known server value.
    if (options && Object.hasOwn(options, "userSelectedLanguage")) {
      this.serverLanguagePreference = this.normalizeServerLanguage(
        options.userSelectedLanguage,
      );
    }

    await this.refreshAvailableLanguages();

    const previousLocale = this.getStoredLanguage() ?? DEFAULT_LANGUAGE_CODE;
    const preferredLocale =
      this.serverLanguagePreference ??
      this.getStoredLanguage() ??
      DEFAULT_LANGUAGE_CODE;
    const locale = resolveLocaleFallback(preferredLocale, DEFAULT_LANGUAGE_CODE);

    // Persist immediately so Accept-Language matches /me preference even
    // while catalog loading is still in flight (or if it falls back).
    this.persistLanguage(locale);

    // Prefer the user's locale via translations API; fall back on failure.
    // Catalog fallback only affects Transloco UI — not Accept-Language storage.
    const appliedLocale = await this.applyLanguageCandidates(
      this.filterCandidatesByAvailability(
        buildLocaleFallbackCandidates(locale, DEFAULT_LANGUAGE_CODE),
      ),
      { showLoader: false, persistToServer: false },
    );

    this.ensureTranslocoLangAvailable(appliedLocale);
    this.transloco.setActiveLang(appliedLocale);

    if (locale !== previousLocale) {
      this.languageAppliedSubject.next(locale);
    }
  }

  async setLanguage(
    locale: string,
    canSwitchLanguage: boolean,
  ): Promise<void> {
    if (!canSwitchLanguage) {
      this.transloco.setActiveLang(DEFAULT_LANGUAGE_CODE);
      return;
    }

    await this.refreshAvailableLanguages();

    const normalizedLocale = this.normalizeLocale(locale);
    const previousLocale = this.getStoredLanguage() ?? DEFAULT_LANGUAGE_CODE;
    const candidates = this.filterCandidatesByAvailability(
      buildLocaleFallbackCandidates(normalizedLocale, previousLocale),
    );

    const appliedLocale = await this.applyLanguageCandidates(candidates, {
      showLoader: true,
      persistToServer: true,
    });

    if (appliedLocale !== normalizedLocale) {
      this.notifyLanguageFallback(normalizedLocale, appliedLocale);
    }

    this.persistLanguage(appliedLocale);
    this.ensureTranslocoLangAvailable(appliedLocale);
    this.transloco.setActiveLang(appliedLocale);

    if (appliedLocale !== previousLocale) {
      this.languageAppliedSubject.next(appliedLocale);
    }
  }

  /**
   * Languages from GET /language-translation/languages (for the preference UI).
   * Triggers a refresh when the cache is still empty.
   */
  async getAvailableLanguages(): Promise<LanguageOption[]> {
    if (!this.availableLanguagesLoaded) {
      await this.refreshAvailableLanguages();
    }
    return [...this.availableLanguages];
  }

  /** Returns the language currently applied in the UI. */
  getEffectiveLanguage(): string {
    return this.getStoredLanguage() ?? DEFAULT_LANGUAGE_CODE;
  }

  getStoredLanguage(): string | null {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return saved && isKnownLanguageCode(saved) ? saved : null;
  }

  /** Clears user language preference and cached translation catalogs. */
  clearLanguageStorage(): void {
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    this.serverLanguagePreference = null;
    this.translationCache.clearAll();
    this.resetAvailableLanguages();
    this.setMissingKeyLogging(false);
  }

  /**
   * Loads selectable locales from the languages API and registers them with Transloco.
   * Failures leave the previous/bootstrap list in place (at least en-US).
   */
  async refreshAvailableLanguages(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.translationApi.getReferenceLanguages(),
      );

      if (response.status !== "success" || response.data.length === 0) {
        this.availableLanguagesLoaded = true;
        return;
      }

      const options: LanguageOption[] = [];
      const codes = new Set<string>([DEFAULT_LANGUAGE_CODE]);

      for (const language of response.data) {
        if (!isKnownLanguageCode(language.code)) {
          continue;
        }
        codes.add(language.code);
        options.push({
          code: language.code,
          nativeName: language.nativeName,
          englishName: language.languageName,
        });
      }

      if (!options.some((lang) => lang.code === DEFAULT_LANGUAGE_CODE)) {
        options.unshift({
          code: DEFAULT_LANGUAGE_CODE,
          nativeName: "English",
          englishName: "English",
        });
      }

      this.availableLanguages = options;
      this.availableLanguageCodes = codes;
      this.availableLanguagesLoaded = true;
      this.transloco.setAvailableLangs([...codes]);
    } catch {
      // Keep bootstrap en-US; picker / switch still attempt translations as needed.
      this.availableLanguagesLoaded = true;
    }
  }

  /** Toggle Transloco's "Missing translation for …" console warnings. */
  private setMissingKeyLogging(enabled: boolean): void {
    this.transloco.config.missingHandler.logMissingKey = enabled;
  }

  private normalizeLocale(locale: string): string {
    return isKnownLanguageCode(locale) ? locale : DEFAULT_LANGUAGE_CODE;
  }

  private normalizeServerLanguage(
    userSelectedLanguage?: string | null,
  ): string | null {
    const trimmed = userSelectedLanguage?.trim();
    return trimmed && isKnownLanguageCode(trimmed) ? trimmed : null;
  }

  private persistLanguage(locale: string): void {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
  }

  private resetAvailableLanguages(): void {
    this.availableLanguages = [];
    this.availableLanguageCodes = new Set<string>([DEFAULT_LANGUAGE_CODE]);
    this.availableLanguagesLoaded = false;
    this.transloco.setAvailableLangs([DEFAULT_LANGUAGE_CODE]);
  }

  /**
   * When the languages API has loaded, only try locales it listed (plus English).
   * Before/without a list, try any valid BCP-47 candidate.
   */
  private filterCandidatesByAvailability(candidates: string[]): string[] {
    if (
      !this.availableLanguagesLoaded ||
      this.availableLanguageCodes.size <= 1
    ) {
      return candidates;
    }

    const filtered = candidates.filter(
      (locale) =>
        locale === DEFAULT_LANGUAGE_CODE ||
        this.availableLanguageCodes.has(locale),
    );

    return filtered.length > 0 ? filtered : [DEFAULT_LANGUAGE_CODE];
  }

  /** Register locale with Transloco so setActiveLang accepts API-driven languages. */
  private ensureTranslocoLangAvailable(locale: string): void {
    const available = this.transloco.getAvailableLangs();
    const codes = available.map((lang) =>
      typeof lang === "string" ? lang : lang.id,
    );
    if (!codes.includes(locale)) {
      this.transloco.setAvailableLangs([...codes, locale]);
    }
  }

  private notifyLanguageFallback(
    requestedLocale: string,
    appliedLocale: string,
  ): void {
    this.messageService.add({
      key: "global",
      severity: "warn",
      summary: TRANSLATION_TOAST_MESSAGES.unavailableTitle,
      detail: TRANSLATION_TOAST_MESSAGES.unavailableDetail(
        getLanguageDisplayName(requestedLocale),
        getLanguageDisplayName(appliedLocale),
      ),
      life: 5000,
    });
  }

  private async applyLanguageCandidates(
    candidates: string[],
    options: { showLoader: boolean; persistToServer: boolean },
  ): Promise<string> {
    if (options.showLoader) {
      this.loader.show();
      this.loader.setMessage(
        this.translateOr(
          TRANSLATION_LOADER_MESSAGES.switchingLanguageKey,
          TRANSLATION_LOADER_MESSAGES.switchingLanguage,
        ),
      );
    }

    try {
      for (const locale of candidates) {
        try {
          await this.loadCatalogForLocale(locale);

          if (options.persistToServer) {
            await firstValueFrom(
              this.translationApi.updateUserLanguage(locale),
            );
          }

          return locale;
        } catch {
          continue;
        }
      }

      // Last resort: English catalog. Caller (setLanguage) shows the
      // "not available / showing …" toast when applied !== requested.
      await this.loadCatalogForLocale(DEFAULT_LANGUAGE_CODE);
      return DEFAULT_LANGUAGE_CODE;
    } finally {
      if (options.showLoader) {
        this.loader.hide();
      }
    }
  }

  private async loadCatalogForLocale(locale: string): Promise<void> {
    const cached = USE_LOCAL_TRANSLATION_FALLBACK
      ? null
      : this.translationCache.get(locale);

    if (cached) {
      this.transloco.setTranslation(
        unflattenTranslationKeys(cached.translations),
        locale,
        { merge: false },
      );
      return;
    }

    const response = await firstValueFrom(
      this.translationApi.getTranslationCatalog(locale),
    );

    if (response.status !== "success") {
      throw new Error(`Translation catalog unavailable for ${locale}`);
    }

    const catalog = response.data;
    this.translationCache.set({
      ...catalog,
      locale,
      cachedAt: new Date().toISOString(),
    });

    this.transloco.setTranslation(
      unflattenTranslationKeys(catalog.translations),
      locale,
      { merge: false },
    );
  }
}
